import { PenLine, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  error: unknown;
  resetError: () => void;
  eventId?: string | null;
}

/**
 * Friendly fallback rendered by `Sentry.ErrorBoundary` when an unhandled
 * render error happens. Shows the Sentry event id so users can quote it
 * to support, but never shows the raw error message (which could include
 * student data from the rendering tree).
 */
export default function SentryFallback({ resetError, eventId }: Props) {
  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-background px-6 text-center">
      <div className="mb-6 flex items-center gap-2">
        <PenLine className="h-8 w-8 text-primary" />
        <span className="text-2xl font-bold tracking-tight text-card-foreground">ClassTrack</span>
      </div>
      <div className="max-w-md w-full bg-card rounded-2xl border border-border shadow-sm p-8">
        <h1 className="text-2xl font-bold text-card-foreground mb-2">Something went wrong</h1>
        <p className="text-muted-foreground text-sm mb-6">
          ClassTrack hit an unexpected error. The team has been notified
          automatically — try reloading the page, and your work is safe.
        </p>
        <Button
          className="w-full gap-2 mb-3"
          size="lg"
          onClick={() => {
            // Navigate to the app root rather than reloading the current URL.
            // Calling resetError() first would trigger a React re-render of the
            // crashed component tree before the reload could fire, potentially
            // re-crashing the boundary and preventing recovery. A fresh
            // navigation to the root always lands on a clean, working state.
            window.location.href = import.meta.env.BASE_URL || "/";
          }}
        >
          <RefreshCw className="h-4 w-4" /> Reload ClassTrack
        </Button>
        {eventId ? (
          <p className="text-xs text-gray-400 mt-4">
            Reference id:{" "}
            <code className="font-mono text-gray-500">{eventId}</code>
            <br />
            Quote it if you contact{" "}
            <a
              href="mailto:classtrack.com.au@gmail.com"
              className="underline hover:text-gray-600"
            >
              support
            </a>
            .
          </p>
        ) : (
          <p className="text-xs text-gray-400 mt-4">
            Need help?{" "}
            <a
              href="mailto:classtrack.com.au@gmail.com"
              className="underline hover:text-gray-600"
            >
              classtrack.com.au@gmail.com
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
